import React from 'react';
import { createRoot } from 'react-dom/client';
import ExecutiveReportTemplate, { ExecutiveReportTemplateData } from '../components/reports/ExecutiveReportTemplate';

export interface ExecutiveReportInput extends ExecutiveReportTemplateData {}

const waitForRender = async () => {
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
};

export async function generateExecutiveReport(input: ExecutiveReportInput): Promise<void> {
  const [{ default: html2canvas }, jsPdfModule] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);
  const jsPDF = jsPdfModule.jsPDF ?? jsPdfModule.default;
  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-99999px';
  host.style.top = '0';
  host.style.width = '1120px';
  host.style.pointerEvents = 'none';
  host.style.opacity = '1';
  host.style.zIndex = '-1';
  document.body.appendChild(host);

  const root = createRoot(host);

  try {
    root.render(<ExecutiveReportTemplate data={input} />);
    await waitForRender();

    const canvas = await html2canvas(host, {
      backgroundColor: '#FFFFFF',
      scale: 2,
      useCORS: true,
      logging: false,
      allowTaint: true,
    });

    const imageData = canvas.toDataURL('image/png', 1.0);
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const margin = 10;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const renderWidth = pageWidth - margin * 2;
    const renderHeight = (canvas.height * renderWidth) / canvas.width;
    const pageContentHeight = pageHeight - margin * 2;

    let remainingHeight = renderHeight;
    let yOffset = margin;

    pdf.addImage(imageData, 'PNG', margin, yOffset, renderWidth, renderHeight, undefined, 'FAST');
    remainingHeight -= pageContentHeight;

    while (remainingHeight > 0) {
      pdf.addPage();
      yOffset = margin - (renderHeight - remainingHeight);
      pdf.addImage(imageData, 'PNG', margin, yOffset, renderWidth, renderHeight, undefined, 'FAST');
      remainingHeight -= pageContentHeight;
    }

    const safeSimulationId = input.simulationId.replace(/[^a-zA-Z0-9-_]/g, '_');
    const dateStamp = new Date().toISOString().slice(0, 10);
    pdf.save(`Relatorio_Impacto_${safeSimulationId}_${dateStamp}.pdf`);
  } finally {
    root.unmount();
    if (host.parentNode) {
      host.parentNode.removeChild(host);
    }
  }
}

export default generateExecutiveReport;
